import { HubConnectionState } from '@microsoft/signalr';
import { ComponentProps, useCallback, useMemo, useRef, useState } from 'react';
import { Modal as RSModal } from 'reactstrap';
import { BulkEditStatus } from 'src/components/common/BulkActions/BulkEditStatus';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import {
  BulkEditStage,
  useBulkEditHubContext,
} from 'src/contexts/BulkEditHubContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Radio, RadioGroup, Stack } from 'src/core/ui';
import { ToggleGroup } from 'src/core/ui/ToggleGroup';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { IconsFill, WarningOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  BulkActionType,
  BulkAdjustCostField,
  BulkChangePriceMode,
  BulkEditPreviewWithDetails,
  BulkEditProgress,
  BulkEditStep,
  Feature,
} from 'src/WebApiController';

import { BulkEditFooter } from '../common/BulkEditFooter';
import { BulkEditHeader } from '../common/BulkEditHeader';
import * as styles from './BulkAdjustCostDialog.css';

const getCostAdjustmentOptions = (
  showOverrideOption: boolean,
  isEventView: boolean | undefined,
  availableInTimeViewtext: string
) => {
  const increaseText = <Content id={ContentId.Increase} />;
  const decreaseText = <Content id={ContentId.Decrease} />;
  const overrideText = isEventView ? (
    <Stack direction="row" gap="s" alignItems="center">
      <WarningOutlineIcon
        fill={IconsFill.textWarning}
        size={vars.iconSize.m}
        title={availableInTimeViewtext}
      />
      <Content id={ContentId.SetOrOverride} />
    </Stack>
  ) : (
    <Content id={ContentId.SetOrOverride} />
  );
  const options = [
    { value: BulkChangePriceMode.Decrease, children: decreaseText },
    { value: BulkChangePriceMode.Increase, children: increaseText },
  ];

  if (showOverrideOption) {
    options.push({
      value: BulkChangePriceMode.Override,
      children: overrideText,
    });
  }

  return options;
};

enum CostAdjustmentMode {
  Percent = 'Percent',
  Absolute = 'Absolute',
}

const PRICE_CHANGE_MODE_OPTIONS = [
  {
    value: CostAdjustmentMode.Absolute,
    children: <Content id={ContentId.AbsoluteAmount} />,
  },
  {
    value: CostAdjustmentMode.Percent,
    children: <Content id={ContentId.Percentage} />,
  },
];

export type BulkAdjustCostDialogProps = ComponentProps<typeof RSModal> & {
  updateKey: string;
  isEventView?: boolean;
  onOkay: (
    adjustmentPercent: number | undefined,
    adjustmentAbsoluteAmount: number | undefined,
    changeMode: BulkChangePriceMode,
    targetField: BulkAdjustCostField,
    supportBackgroundProcess?: boolean,
    onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
    preview?: BulkEditPreviewWithDetails
  ) => void;
  onCancel: () => void;
};

export const BulkAdjustCostDialog = ({
  updateKey,
  isEventView,
  onOkay,
  onCancel,
  ...rest
}: BulkAdjustCostDialogProps) => {
  const { loginContext } = useAppContext();
  const { getUiCurrency } = useLocalizationContext();
  const uiCurrency = useMemo(
    () => getUiCurrency(loginContext?.user?.activeAccount?.currencyCode),
    [getUiCurrency, loginContext?.user?.activeAccount?.currencyCode]
  );

  const hasBulkSetTotalTicketCostFeature = useUserHasFeature(
    Feature.BulkSetTotalTicketCost
  );

  const unitCostText = useContent(ContentId.UnitCost);
  const totalTicketCostText = useContent(ContentId.TotalTicketCost);
  const availableInTimeViewtext = useContent(ContentId.AvailableInTimeView);

  const { setSelectionMode } = useMultiSelectionContext();
  const { setProgress, setPreview, setMainDialogOpened } =
    useBulkEditHubContext();

  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();

  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const [changePriceMode, setChangePriceMode] = useState<BulkChangePriceMode>(
    BulkChangePriceMode.Decrease
  );

  const [costAdjustmentMode, setCostAdjustmentMode] = useState(
    CostAdjustmentMode.Absolute
  );
  const [adjustmentValue, setAdjustmentValue] = useState<number>();

  const placeholderText =
    changePriceMode === BulkChangePriceMode.Override
      ? totalTicketCostText
      : unitCostText;

  const onClose = useCallback(
    async (
      newProgress?: BulkEditProgress,
      closeMainDialogForPopover?: boolean
    ) => {
      if (newProgress?.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        setActivePosEntity(0);

        setSelectionMode(undefined);

        if (closeMainDialogForPopover) {
          setMainDialogOpened(false);
        } else {
          setProgress(undefined);
          setPreview(undefined);
        }

        setIsRefreshing(false);
      }
      onCancel();
    },
    [
      onCancel,
      setActivePosEntity,
      setMainDialogOpened,
      setPreview,
      setProgress,
      setSelectionMode,
    ]
  );

  const onBulkEditDone = useCallback(
    async (doneProgress: BulkEditProgress, finalErrors: string[]) => {
      if (finalErrors.length === 0 && doneProgress.step === BulkEditStep.Done) {
        onClose(doneProgress, true);
      }
    },
    [onClose]
  );

  const { bulkEditHub, progress, preview, initJob, stage } = useBulkEditHub(
    ActionOutboxEntityType.TicketGroup,
    BulkActionType.AdjustCost,
    updateKey,
    onBulkEditDone
  );

  const hasBackgroundBulkEditFeature =
    bulkEditHub?.state === HubConnectionState.Connected;

  const onSubmitHandler = useCallback(
    (onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        onOkay(
          undefined,
          undefined,
          BulkChangePriceMode.Decrease,
          BulkAdjustCostField.UnitCost,
          false,
          onPreviewReceived
        );
      } else {
        // Currently users can't choose. The target field is decided by the change price mode.
        const targetField =
          changePriceMode === BulkChangePriceMode.Override
            ? BulkAdjustCostField.TotalTicketCost
            : BulkAdjustCostField.UnitCost;

        onOkay(
          costAdjustmentMode === CostAdjustmentMode.Percent
            ? adjustmentValue
            : undefined,
          costAdjustmentMode === CostAdjustmentMode.Absolute
            ? adjustmentValue
            : undefined,
          changePriceMode,
          targetField,
          hasBackgroundBulkEditFeature,
          undefined,
          preview
        );
      }
      setIsLoading(false);
    },
    [
      onOkay,
      costAdjustmentMode,
      adjustmentValue,
      changePriceMode,
      hasBackgroundBulkEditFeature,
      preview,
    ]
  );

  const onCostAdjustmentModeChanged = useCallback(
    (newMode: string) => {
      setCostAdjustmentMode(newMode as CostAdjustmentMode);
      if (newMode == CostAdjustmentMode.Percent) {
        if ((adjustmentValue ?? 0) > 99) {
          setAdjustmentValue(99); // we can't decrease by 100 or more % because that will result in zero
        }
      }
    },
    [adjustmentValue]
  );

  const onIncreaseDecreaseChanged = useCallback(
    (value: string) => {
      if (!value) {
        return;
      }
      setChangePriceMode(value as BulkChangePriceMode);
      if (value === BulkChangePriceMode.Override.toString()) {
        onCostAdjustmentModeChanged(CostAdjustmentMode.Absolute);
      }
    },
    [onCostAdjustmentModeChanged]
  );

  return (
    <GenericDialog
      {...rest}
      size={stage === BulkEditStage.Preview ? 'xl' : 'md'}
      header={
        <BulkEditHeader
          headerText={<Content id={ContentId.AdjustPurchaseTicketCost} />}
        />
      }
      onOpened={() => {
        setCostAdjustmentMode(CostAdjustmentMode.Absolute);
        setAdjustmentValue(undefined);
        initJob();
      }}
      onKeyUp={(e) => {
        if (submitButtonRef.current && e.key === 'Enter') {
          submitButtonRef.current.click();
        }
      }}
      onClosed={() => {
        setMainDialogOpened(false);
        if (progress) {
          setSelectionMode(undefined);
        }
        rest.onClosed?.();
      }}
      footer={
        <BulkEditFooter
          entityType={ActionOutboxEntityType.TicketGroup}
          isLoading={isLoading}
          hasBackgroundBulkEditFeature={hasBackgroundBulkEditFeature}
          onClose={onClose}
          onSubmit={onSubmitHandler}
          submittButtonRef={submitButtonRef}
        />
      }
      onCancel={isLoading ? undefined : onCancel}
    >
      <BulkEditStatus
        entityType={ActionOutboxEntityType.TicketGroup}
        isLoading={isRefreshing || isLoading}
        updateKey={updateKey}
        height="full"
      >
        <PosFormField>
          <ToggleGroup
            className={styles.adjustmentOptionToggle}
            disabled={isLoading}
            options={getCostAdjustmentOptions(
              hasBulkSetTotalTicketCostFeature,
              isEventView,
              availableInTimeViewtext
            )}
            // Disabling this in event view because it is possible to choose only some ticket groups of a purchase order.
            // But the unit cost calculation has to be applied to all ticket groups of a purchase order.
            disabledValues={isEventView ? [BulkChangePriceMode.Override] : []}
            value={changePriceMode}
            onValueChange={onIncreaseDecreaseChanged}
          />
        </PosFormField>

        <WarningMessage
          message={
            <Content
              id={
                changePriceMode === BulkChangePriceMode.Override
                  ? ContentId.BulkAdjustCostTotalTicketCostDescription
                  : ContentId.BulkAdjustCostUnitCostDescription
              }
            />
          }
        />

        {changePriceMode !== BulkChangePriceMode.Override && (
          <RadioGroup
            value={costAdjustmentMode}
            defaultValue={CostAdjustmentMode.Percent}
            onValueChange={onCostAdjustmentModeChanged}
            disabled={isLoading}
          >
            <Stack direction="column" gap="m">
              {PRICE_CHANGE_MODE_OPTIONS.map((m) => (
                <Radio
                  key={m.value}
                  value={m.value}
                  label={m.children}
                  disabled={isLoading}
                />
              ))}
            </Stack>
          </RadioGroup>
        )}

        {costAdjustmentMode === CostAdjustmentMode.Percent ? (
          <PosTextField
            value={adjustmentValue}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            disabled={isLoading}
            min={0}
            type="number"
            inputMode="numeric"
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              if (value < 0) return;
              if (value > 99) return;

              setAdjustmentValue(parseFloat(e.target.value));
            }}
            postfixDisplay={'%'}
            placeholder={placeholderText}
          />
        ) : (
          <PosCurrencyField
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            disabled={isLoading}
            uiCurrency={uiCurrency}
            value={adjustmentValue}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              if (value < 0) return;

              setAdjustmentValue(parseFloat(e.target.value));
            }}
            placeholder={placeholderText}
          />
        )}
      </BulkEditStatus>
    </GenericDialog>
  );
};
